"use client";

import { useMemo, useState } from "react";
import { useBusinessWalletTransactions } from "../hooks/query/businessWalletQueries";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  Info,
  MoreVertical,
  Plus,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "./ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { SecuredFooter } from "./ui/secured-footer";
import filterIcon from "../assets/images/trading/filter.svg";
import sortIcon from "../assets/images/trading/sort.svg";
import southAfricaFlag from "../assets/images/forex/south-africa.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "./ui/dropdown-menu";
import {
  TRANSACTION_HISTORY_FILTER_STATUS_OPTIONS,
  TRANSACTION_HISTORY_FILTER_TYPE_OPTIONS,
} from "../constants/transactionHistoryFilters";
import BusinessSuiteTransactionHistoryAllModal from "./BusinessSuiteTransactionHistoryAllModal";
import {
  mapBusinessApiTxToRow,
  resolveBusinessTxStatusTone as resolveStatusTone,
} from "../utils/businessSuiteTransactionRowMap";

const StatusIcon = ({ color }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-[10px] h-[10px]"
  >
    <g clipPath="url(#clip0_bw_th)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.73034 0.302516C5.32699 -0.100838 4.67302 -0.100839 4.26966 0.302515L3.41081 1.16138H2.19412C1.62369 1.16138 1.16127 1.6238 1.16127 2.19423V3.41091L0.302516 4.26966C-0.100838 4.67301 -0.100839 5.32698 0.302515 5.73034L1.16127 6.58909V7.80464C1.16127 8.37507 1.62369 8.8375 2.19412 8.8375H3.40965L4.26966 9.6975C4.67301 10.1009 5.32698 10.1009 5.73034 9.6975L6.59035 8.8375H7.8045C8.37493 8.8375 8.83736 8.37507 8.83736 7.80464V6.59045L9.6975 5.73034C10.1009 5.32699 10.1009 4.67302 9.6975 4.26966L8.83736 3.40955V2.19423C8.83736 1.6238 8.37493 1.16138 7.8045 1.16138H6.5892L5.73034 0.302516Z"
        fill={color}
      />
    </g>
    <defs>
      <clipPath id="clip0_bw_th">
        <rect width="10" height="10" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const BUSINESS_SUITE_TRANSACTION_PREVIEW_ROWS = [
  {
    id: "bw-th-received-eth",
    direction: "received",
    actionLabel: "Received ETH",
    actionLabelMobile: "Received",
    time: "Today, 8:30 AM",
    primaryAmount: "00123.00 ETH",
    secondaryAmount: "R 800.00",
    status: "Awaiting Settlement",
    detail: {
      heroZarAmount: "R 800.00",
      splitPaymentTotalZar: 2450,
      splitPaymentTotalFormatted: "R 2 450.00",
      transactionType: "88Pay",
      statusLabel: "AWAITING SETTLEMENT",
      statusTone: "pending",
      action: "Receive",
      transactionMethod: "PayID",
      receiverPayId: "1582715",
      transactionId:
        "d6ee6c8f9a2b1e4d3c5f6a7b8c9d0e1f2a3b4a9c8d7e6f5a4b3c2d1e0f9a8b7",
      dateLine: "24 January 2024",
      timeLine: "02:00:03 GMT+2",
      serviceCharge: "R 0.00",
      totalCost: "R 800.00",
      showSplitPayment: true,
    },
  },
  {
    id: "bw-th-sent-lte-1",
    direction: "sent",
    actionLabel: "Sent LTE",
    actionLabelMobile: "Sent",
    time: "Today, 8:30 AM",
    primaryAmount: "00456.10 LTE",
    secondaryAmount: "R 20.00",
    status: null,
    detail: {
      heroZarAmount: "R 20.00",
      transactionType: "88Pay",
      statusLabel: "COMPLETE",
      statusTone: "complete",
      action: "Send",
      transactionMethod: "PayID",
      receiverPayId: "8821044",
      transactionId:
        "a1b2c3d4e5f678901234567890abcdef1234567890abcdef1234567890ab",
      dateLine: "24 January 2024",
      timeLine: "02:00:03 GMT+2",
      serviceCharge: "R 0.00",
      totalCost: "R 20.00",
      showSplitPayment: true,
    },
  },
  {
    id: "bw-th-sent-lte-2",
    direction: "sent",
    actionLabel: "Sent LTE",
    actionLabelMobile: "Sent",
    time: "Today, 8:30 AM",
    primaryAmount: "00456.10 LTE",
    secondaryAmount: "R 20.00",
    status: null,
    detail: {
      heroZarAmount: "R 20.00",
      transactionType: "88Pay",
      statusLabel: "COMPLETE",
      statusTone: "complete",
      action: "Send",
      transactionMethod: "PayID",
      receiverPayId: "4412099",
      transactionId:
        "fedcba0987654321fedcba0987654321fedcba0987654321fedcba0987654321",
      dateLine: "23 January 2024",
      timeLine: "18:15:42 GMT+2",
      serviceCharge: "R 0.00",
      totalCost: "R 20.00",
      showSplitPayment: true,
    },
  },
];

function DirectionIcon({ direction }) {
  const IconComponent = direction === "sent" ? ArrowUp : Plus;
  return (
    <div className="w-[40px] h-[40px] rounded-full bg-[#EB3366] flex items-center justify-center">
      <IconComponent className="w-[16px] h-[16px] text-white" />
    </div>
  );
}

function buildSplitPaymentLaunchContext(detail) {
  if (!detail) {
    return null;
  }
  const formatted =
    detail.splitPaymentTotalFormatted ??
    detail.totalCost ??
    detail.heroZarAmount ??
    "";
  let totalZar =
    typeof detail.splitPaymentTotalZar === "number" &&
    Number.isFinite(detail.splitPaymentTotalZar)
      ? detail.splitPaymentTotalZar
      : null;
  if (totalZar === null) {
    const parsed = parseFloat(String(formatted).replace(/[^\d.]/g, ""));
    totalZar = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  if (totalZar === null || totalZar <= 0) {
    return null;
  }
  return {
    totalZar,
    totalZarFormatted:
      formatted && String(formatted).trim()
        ? formatted
        : `R ${totalZar.toFixed(2)}`,
  };
}

function truncateTransactionId(id) {
  if (!id || typeof id !== "string") {
    return "—";
  }
  if (id.length <= 14) {
    return id;
  }
  return `${id.slice(0, 6)}...${id.slice(-4)}`;
}

function DetailStatusBadge({ tone, label }) {
  const resolved = tone || "other";
  const styles =
    resolved === "complete"
      ? "bg-[#E8F9F6] text-[#17C3A5]"
      : resolved === "pending"
        ? "bg-[#EFF1FC] text-[#6375E6]"
        : "bg-[#FFEFD5] text-[#FFC107]";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${styles}`}
    >
      {resolved === "complete" ? (
        <Check className="h-3 w-3 shrink-0 stroke-[3]" aria-hidden />
      ) : (
        <StatusIcon color={resolved === "pending" ? "#6375E6" : "#FFC107"} />
      )}
      {label}
    </span>
  );
}

function BusinessSuiteTransactionDetailDialog({
  row,
  open,
  onOpenChange,
  onSplitPayment,
}) {
  const d = row?.detail;
  if (!d) {
    return null;
  }

  const tone = resolveStatusTone(d.statusLabel, d.statusTone);
  const amountLine = d.listAmount ?? d.heroZarAmount;

  const detailRows = [
    { label: "Amount", value: amountLine },
    { label: "Transaction type", value: d.transactionType },
    {
      label: "Status",
      value: <DetailStatusBadge tone={tone} label={d.statusLabel} />,
      valueIsNode: true,
    },
    { label: "Action", value: d.action },
    { label: "Transaction Method", value: d.transactionMethod },
    ...(d.receiverPayId
      ? [{ label: "Receiver PayID", value: d.receiverPayId }]
      : []),
    {
      label: "Transaction ID",
      value: truncateTransactionId(d.transactionId),
    },
    {
      label: "Date",
      value: d.dateLine,
      secondaryValue: d.timeLine,
    },
    {
      label: "Service charge",
      value: d.serviceCharge,
      helperText: "Fees may apply depending on your plan.",
    },
    {
      label: "Total cost",
      value: d.totalCost,
      emphasize: true,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[978px] gap-0 border border-[#EBE9EF] bg-white p-0 shadow-2xl rounded-[24px]">
        <DialogTitle className="sr-only">Transaction details</DialogTitle>
        <DialogDescription className="sr-only">
          Amount, status, and identifiers for this transaction.
        </DialogDescription>

        <div className="relative flex flex-col">
          <div className="w-[90%] mx-auto">
            <div className="flex items-center justify-between pt-10 px-6">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584] transition-colors hover:bg-[#F5F4F7]"
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584] transition-colors hover:bg-[#F5F4F7]"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-6 pb-10 pt-10">
              <div className="rounded-[24px] bg-[#F5F4F7] px-5 py-6 h-[142px] flex items-center justify-center">
                <div className="flex items-center justify-center gap-3">
                  <img
                    src={southAfricaFlag}
                    alt=""
                    className="h-10 w-10 shrink-0 rounded-full object-cover"
                  />
                  <span className="text-[40px] font-bold leading-none tracking-[-0.02em] text-[#423E51]">
                    {d.heroZarAmount}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <p className="text-[14px] font-semibold leading-5 text-[#6D6584]">
                  Transaction details
                </p>
                {d.showSplitPayment ? (
                  <button
                    type="button"
                    className="shrink-0 rounded-full px-4 py-2 text-[14px] h-[48px] font-semibold text-white transition-opacity hover:opacity-90"
                    style={{
                      backgroundImage:
                        "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
                    }}
                    onClick={() => {
                      onOpenChange(false);
                      const ctx = buildSplitPaymentLaunchContext(d);
                      onSplitPayment?.(ctx);
                    }}
                  >
                    Split payment
                  </button>
                ) : null}
              </div>

              <div className="mt-3 w-full rounded-2xl border border-[#EBE9EF] bg-white py-1">
                {detailRows.map((item, index) => (
                  <div key={`${item.label}-${index}`}>
                    <div className="flex items-center justify-between gap-4 px-4 py-5">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className={`text-[16px] leading-5 ${
                            item.emphasize
                              ? "font-semibold text-[#423E51]"
                              : "font-normal text-[#6D6584]"
                          }`}
                        >
                          {item.label}
                        </span>
                        {item.helperText ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#EB3366]"
                                  aria-label={`${item.label} information`}
                                >
                                  <Info className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-[220px] text-xs"
                              >
                                {item.helperText}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : null}
                      </div>
                      <div className="max-w-[58%] text-right">
                        {item.valueIsNode ? (
                          <div className="flex justify-end">{item.value}</div>
                        ) : (
                          <p
                            className={`break-words text-[20px] leading-5 text-[#423E51] ${
                              item.emphasize ? "font-bold" : "font-semibold"
                            }`}
                          >
                            {item.value}
                          </p>
                        )}
                        {item.secondaryValue ? (
                          <p className="mt-1 text-[13px] leading-4 text-[#6D6584]">
                            {item.secondaryValue}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    {index < detailRows.length - 1 ? (
                      <div className="h-px w-full bg-[#EBE9EF]" />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SecuredFooter className="mt-0 border-t border-[#EBE9EF] p-6" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BusinessSuiteTransactionHistory({
  assetFocusView = false,
  businessId = null,
  onSplitPayment,
}) {
  const {
    data: txPage,
    isLoading,
    isError,
  } = useBusinessWalletTransactions(businessId, {
    page: 1,
    limit: 20,
    sort: "date_desc",
  });

  const displayRows = useMemo(() => {
    const raw = Array.isArray(txPage?.results) ? txPage.results : [];
    const mapped = raw.map(mapBusinessApiTxToRow).filter((row) => row != null);
    if (mapped.length > 0) {
      return mapped;
    }
    if (businessId) {
      return [];
    }
    return BUSINESS_SUITE_TRANSACTION_PREVIEW_ROWS;
  }, [txPage, businessId]);

  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date-desc");
  const [detailRow, setDetailRow] = useState(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);

  const handleSelectFromAllModal = (row) => {
    if (row?.detail) {
      setDetailRow(row);
    }
  };

  const openTransactionDetail = (row) => {
    if (row?.detail) {
      setDetailRow(row);
    }
  };

  const handleDetailOpenChange = (nextOpen) => {
    if (!nextOpen) {
      setDetailRow(null);
    }
  };

  return (
    <Card className="flex flex-col md:pt-2 pt-0 mb:pb-6 pb-0">
      <CardHeader className="md:pb-6 pb-2">
        <CardTitle>
          <div className="flex justify-between items-center">
            <div className="text-[#6D6584] text-[20px] font-semibold">
              Transaction History
            </div>
            <div className="flex items-center justify-end gap-2">
              {assetFocusView ? (
                <div className="items-center gap-2 flex">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={`h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2 ${
                          filterType !== "all" || filterStatus !== "all"
                            ? "border-[#EB3366] bg-[#EB3366]/5"
                            : ""
                        }`}
                      >
                        <span>Filter</span>
                        <img
                          src={filterIcon}
                          alt="filter"
                          className="w-[16px] h-[16px] text-[#6D6584]"
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                      <DropdownMenuRadioGroup
                        value={filterType}
                        onValueChange={setFilterType}
                      >
                        {TRANSACTION_HISTORY_FILTER_TYPE_OPTIONS.map(
                          ({ value, label }) => (
                            <DropdownMenuRadioItem key={value} value={value}>
                              {label}
                            </DropdownMenuRadioItem>
                          )
                        )}
                      </DropdownMenuRadioGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                      <DropdownMenuRadioGroup
                        value={filterStatus}
                        onValueChange={setFilterStatus}
                      >
                        {TRANSACTION_HISTORY_FILTER_STATUS_OPTIONS.map(
                          ({ value, label }) => (
                            <DropdownMenuRadioItem key={value} value={value}>
                              {label}
                            </DropdownMenuRadioItem>
                          )
                        )}
                      </DropdownMenuRadioGroup>
                      {(filterType !== "all" || filterStatus !== "all") && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setFilterType("all");
                              setFilterStatus("all");
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            Clear Filters
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={`h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2 ${
                          sortBy !== "date-desc"
                            ? "border-[#EB3366] bg-[#EB3366]/5"
                            : ""
                        }`}
                      >
                        <span>Sort</span>
                        <img
                          src={sortIcon}
                          alt="sort"
                          className="w-[16px] h-[16px] text-[#6D6584]"
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                      <DropdownMenuRadioGroup
                        value={sortBy}
                        onValueChange={setSortBy}
                      >
                        <DropdownMenuRadioItem value="date-desc">
                          Date (Newest First)
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="date-asc">
                          Date (Oldest First)
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="amount-desc">
                          Amount (Highest First)
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="amount-asc">
                          Amount (Lowest First)
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  disabled={!businessId}
                  onClick={() => setViewAllOpen(true)}
                  className="h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2 disabled:opacity-50"
                >
                  <span>View All</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0 md:px-5 flex flex-col flex-1">
        <div className="md:space-y-7 space-y-3 p-6 pt-4 flex-1">
          {businessId && isLoading && (
            <p className="flex items-center justify-center py-8 text-[#6D6584] text-sm">
              Loading transactions…
            </p>
          )}
          {businessId && !isLoading && isError && (
            <p className="flex items-center justify-center py-8 text-[#6D6584] text-sm text-center">
              We couldn’t load business transactions. Try again in a moment.
            </p>
          )}
          {!isLoading &&
            !(businessId && isError) &&
            displayRows.length > 0 &&
            displayRows.map((row) => {
              const status = row.status;
              return (
                <div
                  key={row.id}
                  className={`relative flex items-center justify-between gap-4 md:grid md:grid-cols-12 md:gap-4 md:border-none border border-[#F1EDF7] border-[1px] rounded-2xl p-4 md:p-0 ${
                    row.detail ? "pr-14 md:pr-0" : ""
                  }`}
                >
                  {row.detail ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="absolute right-3 top-3 h-9 w-9 rounded-full border-[#EBE9EF] md:hidden"
                      onClick={() => openTransactionDetail(row)}
                      aria-label="View transaction details"
                    >
                      <ArrowRight className="h-4 w-4 text-[#6D6584]" />
                    </Button>
                  ) : null}
                  <div className="col-span-4 flex items-center gap-3 min-w-0">
                    <div className="relative flex-shrink-0">
                      <DirectionIcon direction={row.direction} />
                    </div>
                    <div className="min-w-0 flex-1 md:space-y-2 space-y-1">
                      <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                        <span className="md:hidden">
                          {row.actionLabelMobile}
                        </span>
                        <span className="hidden md:inline">
                          {row.actionLabel}
                        </span>
                      </div>
                      <div className="text-[#6D6584] text-xs md:text-[14px]">
                        {row.time}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-4 hidden md:flex flex-col space-y-2">
                    <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                      {row.primaryAmount}
                    </div>
                    {row.secondaryAmount && (
                      <div className="text-[#6D6584] text-xs md:text-[14px]">
                        {row.secondaryAmount}
                      </div>
                    )}
                  </div>

                  <div className="md:hidden flex flex-col items-end">
                    <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                      {row.primaryAmount}
                    </div>
                    {row.secondaryAmount && (
                      <div className="text-[#6D6584] text-xs md:text-[14px]">
                        {row.secondaryAmount}
                      </div>
                    )}
                    {status && (
                      <div className="flex items-center gap-2 text-[14px] font-bold mt-1">
                        {status === "Pending" ||
                        status === "Awaiting Settlement" ? (
                          <div className="text-[10px] font-bold text-[#6375E6]">
                            {status.toUpperCase()}
                          </div>
                        ) : status === "Complete" || status === "Completed" ? (
                          <div className="text-[10px] font-bold text-[#17C3A5]">
                            {status.toUpperCase()}
                          </div>
                        ) : (
                          <div className="text-[10px] font-bold text-[#FFC107]">
                            {status.toUpperCase()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="col-span-2 hidden md:block">
                    {status && (
                      <div className="flex items-center gap-2 text-[14px] font-bold">
                        {status === "Pending" ||
                        status === "Awaiting Settlement" ? (
                          <div className="flex items-center gap-2 bg-[#EFF1FC] px-3 py-1 rounded-full">
                            <StatusIcon color="#6375E6" />
                            <span className="text-[10px] font-bold text-[#6375E6]">
                              {status.toUpperCase()}
                            </span>
                          </div>
                        ) : status === "Complete" || status === "Completed" ? (
                          <div className="flex items-center gap-2 bg-[#E8F9F6] px-3 py-1 rounded-full">
                            <StatusIcon color="#17C3A5" />
                            <span className="text-[10px] font-bold text-[#17C3A5]">
                              {status.toUpperCase()}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 bg-[#FFEFD5] px-3 py-1 rounded-full">
                            <StatusIcon color="#FFC107" />
                            <span className="text-[10px] font-bold text-[#FFC107]">
                              {status.toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="col-span-2 justify-end hidden md:flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="rounded-full flex-shrink-0 border-gray-200 hover:bg-gray-200 h-10 w-10"
                      aria-label="More options"
                    >
                      <MoreVertical className="w-4 h-4 text-[#6D6584]" />
                    </Button>
                    <Button
                      type="button"
                      onClick={() => openTransactionDetail(row)}
                      variant="outline"
                      size="icon"
                      className="rounded-full flex-shrink-0 border-gray-200 hover:bg-gray-200 h-10 w-10"
                      aria-label="View details"
                    >
                      <ArrowRight className="w-4 h-4 text-[#6D6584]" />
                    </Button>
                  </div>
                </div>
              );
            })}
          {!isLoading &&
            !(businessId && isError) &&
            displayRows.length === 0 && (
              <p className="flex items-center justify-center py-8 text-[#6D6584]">
                No transactions found.
              </p>
            )}
        </div>
      </CardContent>

      <BusinessSuiteTransactionDetailDialog
        row={detailRow}
        open={Boolean(detailRow)}
        onOpenChange={handleDetailOpenChange}
        onSplitPayment={onSplitPayment}
      />

      {businessId ? (
        <BusinessSuiteTransactionHistoryAllModal
          open={viewAllOpen}
          onOpenChange={setViewAllOpen}
          businessId={businessId}
          onSelectRow={handleSelectFromAllModal}
        />
      ) : null}
    </Card>
  );
}

export default BusinessSuiteTransactionHistory;
